import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';

// Base64 encoded logo - can be replaced with actual logo
const LOGO_BASE64 = null; // Replace with actual base64 encoded logo if available

// Add number formatting function
function formatNumber(value) {
  return new Intl.NumberFormat('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

/**
 * Generate a PDF for a quotation on the client side
 * @param {Object} quotation - The quotation object with items
 * @returns {Promise<Blob>} - The PDF as a blob
 */
export async function generateQuotationPDF(quotation) {
  console.log('Generating client-side PDF for quotation:', quotation.quotation_number);
  console.log('Quotation items data:', quotation.items);
  
  // Validate quotation object
  if (!quotation || typeof quotation !== 'object') {
    throw new Error('Invalid quotation data provided');
  }
  
  // Create a new PDF document with compression enabled
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
    putOnlyUsedFonts: true,
    precision: 2
  });
  
  // Set initial coordinates
  let y = 10;
  const leftMargin = 15;
  const rightMargin = 195;
  const pageWidth = 210;
  const contentWidth = pageWidth - leftMargin - (pageWidth - rightMargin);
  
  // Add company header with dark blue background
  doc.setFillColor(0, 32, 96); // Darker blue header - consistent with SOA and Invoice
  doc.rect(0, 0, pageWidth, 40, 'F'); // Increased height to match SOA and Invoice
  
  // Add logo if available - with proper proportions and compression
  try {
    const logoUrl = new URL('@/assets/logo.png', import.meta.url).href;
    const img = new Image();
    img.src = logoUrl;
    doc.addImage(img, 'PNG', leftMargin, (40 - 25) / 2, 25, 25, 'logo', 'MEDIUM', 0);
  } catch (error) {
    console.error('Error loading logo:', error);
  }
  
  // Add company details
  doc.setTextColor(255, 255, 255); // White text
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24); // Larger font size to match SOA and Invoice
  
  // Calculate vertical center of header with adjusted spacing for text blocks
  const centerY = 40 / 2;
  const addressLineHeight = 5; // Height between address lines
  
  // Company name positioned at proper height
  doc.text('IGW Digital Printing Services', 105, centerY - 8, { align: 'center' });
  
  // Company details with improved spacing below company name
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text('27 A Dacudao Ave, Poblacion District', 105, centerY + 3, { align: 'center' });
  doc.text('Davao City, Philippines', 105, centerY + 3 + addressLineHeight, { align: 'center' });
  doc.text('Tel: 0927-426-2634', 105, centerY + 3 + (addressLineHeight * 2), { align: 'center' });
  
  // Reset text color for the rest of the document
  doc.setTextColor(0, 0, 0);
  
  // Add title with more spacing
  y = 50; // Increased spacing from header
  doc.setFontSize(20); // Larger font size to match SOA and Invoice
  doc.setFont('helvetica', 'bold');
  doc.text('Formal Quotation', pageWidth / 2, y, { align: 'center' });
  
  // Client and Quotation details in rounded rectangles like Invoice
  y += 10;
  
  // Left side - Client Details
  doc.setDrawColor(220, 220, 220);
  doc.setFillColor(247, 248, 250);
  doc.roundedRect(leftMargin, y, 82, 35, 2, 2, 'FD');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('CLIENT DETAILS:', leftMargin + 5, y + 8);
  doc.setFont('helvetica', 'normal');
  
  // Determine client name based on available fields
  let clientName = '';
  const firstName = quotation.first_name || '';
  const lastName = quotation.last_name || '';
  
  if (firstName || lastName) {
    clientName = `${firstName} ${lastName}`.trim();
  } else if (quotation.company_name) {
    clientName = quotation.company_name;
  } else if (quotation.client_name) {
    clientName = quotation.client_name;
  } else {
    clientName = 'N/A';
  }
  
  // Client details with better spacing and conditional rendering
  let detailsY = y + 15;
  const lineHeight = 4;
  const maxWidth = 70; // Maximum width for text in the Client Details box
  
  // Name
  if (clientName && clientName !== 'N/A') {
    const nameLines = doc.splitTextToSize(clientName, maxWidth);
    doc.text(nameLines, leftMargin + 5, detailsY);
    detailsY += nameLines.length * lineHeight;
  }
  
  // Company Name (if different from client name)
  const companyName = quotation.company_name || '';
  if (companyName && companyName !== clientName) {
    const companyLines = doc.splitTextToSize(companyName, maxWidth);
    doc.text(companyLines, leftMargin + 5, detailsY);
    detailsY += companyLines.length * lineHeight;
  }
  
  // Phone
  const clientPhone = quotation.client_phone || quotation.phone || quotation.contact_number || '';
  if (clientPhone) {
    const phoneText = `Tel: ${clientPhone}`;
    const phoneLines = doc.splitTextToSize(phoneText, maxWidth);
    doc.text(phoneLines, leftMargin + 5, detailsY);
    detailsY += phoneLines.length * lineHeight;
  }
  
  // Email
  const clientEmail = quotation.client_email || quotation.email || '';
  if (clientEmail) {
    const emailText = `Email: ${clientEmail}`;
    const emailLines = doc.splitTextToSize(emailText, maxWidth);
    doc.text(emailLines, leftMargin + 5, detailsY);
  }
  
  // Right side - Quotation Details
  doc.setFillColor(247, 248, 250);
  doc.roundedRect(leftMargin + 88, y, 82, 35, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold');
  doc.text('QUOTATION DETAILS:', leftMargin + 93, y + 8);
  doc.setFont('helvetica', 'normal');
  
  let quotationDetailsY = y + 15;
  const quotationLineHeight = 4;
  
  // Quotation Number
  doc.text(`Quotation #: ${quotation.quotation_number || 'N/A'}`, leftMargin + 93, quotationDetailsY);
  quotationDetailsY += quotationLineHeight;
  
  // Date
  const quoteDate = quotation.quote_date ? new Date(quotation.quote_date) : new Date();
  const quoteDateStr = `${quoteDate.toLocaleString('default', { month: 'short' })} ${quoteDate.getDate()}, ${quoteDate.getFullYear()}`;
  doc.text(`Date: ${quoteDateStr}`, leftMargin + 93, quotationDetailsY);
  quotationDetailsY += quotationLineHeight;
  
  // Valid Until
  const validUntil = quotation.valid_until ? new Date(quotation.valid_until) : new Date();
  const validUntilStr = `${validUntil.toLocaleString('default', { month: 'short' })} ${validUntil.getDate()}, ${validUntil.getFullYear()}`;
  doc.text(`Valid Until: ${validUntilStr}`, leftMargin + 93, quotationDetailsY);
  quotationDetailsY += quotationLineHeight;
  
  // Status
  const status = quotation.status || 'pending';
  const statusText = status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
  doc.text(`Status: ${statusText}`, leftMargin + 93, quotationDetailsY);
  
  // Items table header - match Invoice styling
  y += 40;
  
  // Add "Items:" title like Invoice
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Items:', leftMargin, y);
  
  y += 6;
  
  doc.setFillColor(0, 32, 96);
  doc.rect(leftMargin, y, contentWidth, 9, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  
  // Decide if any discounts exist to control columns
  const itemsArray = Array.isArray(quotation.items) ? quotation.items : [];
  const hasAnyDiscount = itemsArray.some(it => {
    if (!it) return false;
    const hasType = it.discount_type && it.discount_type !== 'none';
    return Number(it.discount_amount || 0) > 0 || (hasType && Number(it.discount_value || 0) > 0);
  });

  // Column definitions - adapt when no discounts to avoid cramped totals
  let cols;
  if (hasAnyDiscount) {
    cols = {
      desc: { x: leftMargin + 2, w: 60, text: 'Description', align: 'left' },
      qty: { x: leftMargin + 64, w: 14, text: 'Qty', align: 'center' },
      price: { x: leftMargin + 80, w: 18, text: 'Unit Price', align: 'right' },
      discountPerUnit: { x: leftMargin + 100, w: 16, text: 'Disc/Unit', align: 'right' },
      totalDiscount: { x: leftMargin + 118, w: 18, text: 'Total Disc', align: 'right' },
      subtotal: { x: leftMargin + 138, w: 18, text: 'Pre-Disc', align: 'right' },
      total: { x: leftMargin + 158, w: 18, text: 'Total', align: 'right' }
    };
  } else {
    cols = {
      desc: { x: leftMargin + 2, w: 80, text: 'Description', align: 'left' },
      qty: { x: leftMargin + 84, w: 16, text: 'Quantity', align: 'center' },
      price: { x: leftMargin + 102, w: 26, text: 'Unit Price', align: 'right' },
      subtotal: { x: leftMargin + 130, w: 26, text: 'Subtotal', align: 'right' },
      total: { x: leftMargin + 158, w: 20, text: 'Total', align: 'right' }
    };
  }
  
  // Draw header texts
  Object.values(cols).forEach(col => {
    let x;
    if (col.align === 'left') {
      x = col.x + 1;
    } else if (col.align === 'right') {
      x = col.x + col.w - 1;
    } else {
      x = col.x + col.w/2;
    }
    doc.text(col.text, x, y + 5.5, { align: col.align });
  });
  
  // Reset text color for content
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  
  // Add items with alternating row backgrounds like Invoice
  y += 9;
  let totalAmount = 0;
  let subtotalBeforeDiscount = 0;
  let totalDiscount = 0;
  
  // itemsArray defined above
  
  for (let i = 0; i < itemsArray.length; i++) {
    const item = itemsArray[i];
    
    // Skip invalid items
    if (!item || typeof item !== 'object') continue;
    
    // Check if we need a new page
    if (y > 250) {
      doc.addPage();
      y = 20;
      
      // Add simplified header to new page
      doc.setFillColor(0, 32, 96);
      doc.rect(leftMargin, y, contentWidth, 9, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      
      Object.values(cols).forEach(col => {
        let x;
        if (col.align === 'left') {
          x = col.x + 1;
        } else if (col.align === 'right') {
          x = col.x + col.w - 1;
        } else {
          x = col.x + col.w/2;
        }
        doc.text(col.text, x, y + 5.5, { align: col.align });
      });
      
      y += 9;
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
    }
    
    // Add alternating row background
    if (i % 2 === 0) {
      doc.setFillColor(248, 249, 250);
      doc.rect(leftMargin, y, contentWidth, 7, 'F');
    }
    
    // Build description with dimensions
    let description = item.description || 'Unknown Product';
    if (item.width && item.height && item.dimension_unit && 
        !isNaN(parseFloat(item.width)) && !isNaN(parseFloat(item.height))) {
      const width = parseFloat(item.width).toFixed(2);
      const height = parseFloat(item.height).toFixed(2);
      description += ` (${width}x${height}${item.dimension_unit})`;
    }
    
    const wrappedDesc = doc.splitTextToSize(description, cols.desc.w);
    const lineHeight = 3.8;
    const rowHeight = Math.max(7, wrappedDesc.length * lineHeight);
    
    // Description
    doc.text(wrappedDesc, cols.desc.x, y + 4);
    
    // Use pre-calculated values from backend
    const quantity = parseFloat(item.quantity || 1);
    const unitPrice = parseFloat(item.unit_price || 0);
    
    // Use backend pre-calculated values
    const itemSubtotal = parseFloat(item.pre_discount_subtotal || 0);
    let discountAmount = parseFloat(item.discount_amount || 0);
    const itemTotal = parseFloat(item.subtotal || 0);
    
    // Fallback calculation if backend values are missing
    let calculatedSubtotal = itemSubtotal;
    let calculatedDiscount = discountAmount;
    let calculatedTotal = itemTotal;
    
    if (!itemSubtotal || itemSubtotal === 0) {
      if (item.width && item.height && 
          !isNaN(parseFloat(item.width)) && !isNaN(parseFloat(item.height))) {
        // Dimensional item: area × unit_price × quantity
        const area = parseFloat(item.width) * parseFloat(item.height);
        calculatedSubtotal = area * unitPrice * quantity;
      } else {
        // Non-dimensional item: unit_price × quantity
        calculatedSubtotal = unitPrice * quantity;
      }
    }
    
    // Calculate discount based on per-unit discount logic
    if (item.discount_type && item.discount_type !== 'none' && item.discount_value && item.discount_value > 0) {
      if (item.discount_type === 'percentage') {
        // Percentage discount applied to unit price
        const unitDiscountAmount = (unitPrice * parseFloat(item.discount_value)) / 100;
        calculatedDiscount = unitDiscountAmount * quantity;
        if (item.is_dimensions && item.width && item.height) {
          // For dimensional items, multiply by area
          calculatedDiscount = calculatedDiscount * (item.width * item.height);
        }
      } else if (item.discount_type === 'fixed') {
        // Fixed discount per unit
        calculatedDiscount = parseFloat(item.discount_value) * quantity;
        if (item.is_dimensions && item.width && item.height) {
          // For dimensional items, multiply by area
          calculatedDiscount = calculatedDiscount * (item.width * item.height);
        }
        // Ensure discount doesn't exceed the subtotal
        calculatedDiscount = Math.min(calculatedDiscount, calculatedSubtotal);
      }
    }
    
    if (!itemTotal || itemTotal === 0) {
      calculatedTotal = calculatedSubtotal - calculatedDiscount;
    }
    
    // Display values using calculated amounts
    doc.text(quantity.toString(), cols.qty.x + cols.qty.w/2, y + 4, { align: 'center' });
    doc.text(`P ${formatNumber(unitPrice)}`, cols.price.x + cols.price.w - 1, y + 4, { align: 'right' });
    
    if (hasAnyDiscount) {
      // Display per-unit discount
      if (item.discount_type && item.discount_type !== 'none' && item.discount_value && item.discount_value > 0) {
        if (item.discount_type === 'percentage') {
          doc.text(`${item.discount_value}%`, cols.discountPerUnit.x + cols.discountPerUnit.w - 1, y + 4, { align: 'right' });
        } else {
          doc.text(`P ${formatNumber(item.discount_value)}`, cols.discountPerUnit.x + cols.discountPerUnit.w - 1, y + 4, { align: 'right' });
        }
      } else {
        doc.text('-', cols.discountPerUnit.x + cols.discountPerUnit.w - 1, y + 4, { align: 'right' });
      }
      
      // Display total discount
      doc.text(`P ${formatNumber(calculatedDiscount)}`, cols.totalDiscount.x + cols.totalDiscount.w - 1, y + 4, { align: 'right' });
      
      // Display pre-discount subtotal
      doc.text(`P ${formatNumber(calculatedSubtotal)}`, cols.subtotal.x + cols.subtotal.w - 1, y + 4, { align: 'right' });
    } else {
      // When no discounts, show subtotal in the subtotal column
      doc.text(`P ${formatNumber(calculatedSubtotal)}`, cols.subtotal.x + cols.subtotal.w - 1, y + 4, { align: 'right' });
    }
    
    doc.text(`P ${formatNumber(calculatedTotal)}`, cols.total.x + cols.total.w - 1, y + 4, { align: 'right' });
    
    totalAmount += calculatedTotal;
    subtotalBeforeDiscount += calculatedSubtotal;
    totalDiscount += calculatedDiscount;
    
    y += rowHeight;
  }
  
  // Add totals section like Invoice
  const summaryWidth = 80;
  const summaryX = leftMargin + contentWidth - summaryWidth;
  
  // Calculate final total
  const finalTotal = subtotalBeforeDiscount - totalDiscount;
  const showDiscountLine = totalDiscount > 0 && hasAnyDiscount;
  const summaryLines = showDiscountLine ? 3 : 2;
  const summaryHeight = 5 + (summaryLines * 5);

  // Prevent overlap with following sections
  if (y + summaryHeight + 10 > 280) {
    doc.addPage();
    y = 20;
  }
  
  doc.setFillColor(0, 32, 96);
  doc.roundedRect(summaryX, y, summaryWidth, summaryHeight, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  
  const labelX = summaryX + 5;
  const valueX = summaryX + summaryWidth - 5;
  
  // Subtotal
  doc.text('Subtotal:', labelX, y + 5);
  doc.text(`P ${formatNumber(subtotalBeforeDiscount)}`, valueX, y + 5, { align: 'right' });
  
  // Discount (only when present)
  if (showDiscountLine) {
    doc.text('Discount:', labelX, y + 10);
    doc.text(`P ${formatNumber(totalDiscount)}`, valueX, y + 10, { align: 'right' });
  }
  
  // Total Amount
  const totalY = y + (showDiscountLine ? 15 : 10);
  doc.text('Total Amount:', labelX, totalY);
  doc.text(`P ${formatNumber(finalTotal)}`, valueX, totalY, { align: 'right' });
  
  // Reset text color
  doc.setTextColor(0, 0, 0);
  
  // Advance cursor below totals box to avoid overlap with following sections
  y = (totalY + 2); // 5 for last line height, +8 margin
  
  // Add notes section if notes exist
  if (quotation.notes && quotation.notes.trim()) {
    y += 25;
    
    // Notes section with light background
    doc.setDrawColor(220, 220, 220);
    doc.setFillColor(252, 252, 252);
    doc.roundedRect(leftMargin, y, contentWidth, 12, 2, 2, 'FD');
    
    // Notes header
    doc.setTextColor(0, 32, 96);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Notes:', leftMargin + 5, y + 6);
    
    // Notes content
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(quotation.notes, leftMargin + 15, y + 6, { maxWidth: contentWidth - 20 });
    
    y += 5;
  }
  
  // Add terms and conditions section like Invoice
  y += 10;
  
  // Create compact enclosed section with two columns
  doc.setTextColor(0, 32, 96);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setDrawColor(220, 220, 220);
  doc.setFillColor(252, 252, 252);
  doc.roundedRect(leftMargin, y, contentWidth, 47, 2, 2, 'FD');
  
  // Calculate column positions
  const columnWidth = (contentWidth - 20) / 2; // Equal width for both columns, with 20mm spacing between
  const termsX = leftMargin;
  const paymentsX = leftMargin + columnWidth + 20; // 20mm gap between columns
  
  // Left column - Terms and Conditions
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Terms and Conditions:', termsX + 5, y + 6);
  
  const termsLineHeight = 3.5;
  
  // Working Days
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('Working Days:', termsX + 5, y + 12);
  doc.setFont('helvetica', 'normal');
  doc.text('• Number of working days depending on the volume of orders.', termsX + 10, y + 12 + termsLineHeight);
  doc.text('• Start production after payment of Down payment and approval of lay-out.', termsX + 10, y + 12 + (termsLineHeight * 2));
  
  // Payment Terms
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Terms:', termsX + 5, y + 12 + (termsLineHeight * 4));
  doc.setFont('helvetica', 'normal');
  doc.text('• 50% Down payment before design lay-out and start production', termsX + 10, y + 12 + (termsLineHeight * 5));
  doc.text('• Balance payment after the project is completely done.', termsX + 10, y + 12 + (termsLineHeight * 6));
  
  // Right column - MOP
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('IGW Official MOP (Mode of Payments):', paymentsX + 5, y + 6);
  
  const paymentLineHeight = 3.5;
  
  // Bank details
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text('Bank Transfer:', paymentsX + 5, y + 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('Bank Name: Bank of the Philippines Island (BPI)', paymentsX + 10, y + 12 + paymentLineHeight);
  doc.text('Account Name: IGW Digital Printing Services', paymentsX + 10, y + 12 + (paymentLineHeight * 2));
  doc.text('Account Number: 2230005527', paymentsX + 10, y + 12 + (paymentLineHeight * 3));
  
  // Gcash details
  doc.setFont('helvetica', 'bold');
  doc.text('GCash:', paymentsX + 5, y + 12 + (paymentLineHeight * 4));
  doc.setFont('helvetica', 'normal');
  doc.text('Number: 09272940832', paymentsX + 10, y + 12 + (paymentLineHeight * 5));
  
  // Other payment methods
  doc.setFont('helvetica', 'bold');
  doc.text('We Also Accept:', paymentsX + 5, y + 12 + (paymentLineHeight * 6));
  doc.setFont('helvetica', 'normal');
  doc.text('• Credit/Debit Cards (Terminal)', paymentsX + 10, y + 12 + (paymentLineHeight * 7));
  doc.text('• Paypal', paymentsX + 10, y + 12 + (paymentLineHeight * 8));
  doc.text('• Cheques', paymentsX + 10, y + 12 + (paymentLineHeight * 9.5));
  
  // Set y for the next section
  y += 55;
  
  // Add signature section
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Acceptance:', leftMargin, y);
  y += 8;
  
  doc.setFont('helvetica', 'normal');
  doc.text('Client Signature: _______________________', leftMargin, y);
  doc.text('Date: _______________', leftMargin + 100, y);
  
  y += 8;
  doc.text('Print Name: _______________________', leftMargin, y);
  doc.text('Position: _______________', leftMargin + 100, y);
  
  // Add footer with contact information like Invoice
  y += 20;
  doc.setFontSize(7);
  doc.setTextColor(70, 70, 70);
  doc.text('If you have any questions about this quotation, please contact:', pageWidth / 2, y, { align: 'center' });
  
  y += 3;
  doc.setFont('helvetica', 'bold');
  doc.text('Murphy John O. Ong, Proprietor', pageWidth / 2, y, { align: 'center' });
  
  y += 3;
  doc.setFont('helvetica', 'normal');
  doc.text('Phone: +63 991 928 5008 | Telephone: (082) 308-0310 | Email: murphyong@igwprints.com', pageWidth / 2, y, { align: 'center' });
  
  // Thank you message
  y += 5;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 32, 96);
  doc.setFontSize(9);
  doc.text('Thank You For Your Business!', pageWidth / 2, y, { align: 'center' });
  
  // Add page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(70, 70, 70);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
    doc.text('IGW Sales & Services - Quotation', pageWidth / 2, 295, { align: 'center' });
  }
  
  // Return the PDF as a blob with compression
  return doc.output('blob', {
    compress: true,
    putOnlyUsedFonts: true,
    precision: 2,
    imageCompression: 'MEDIUM'
  });
}

// Helper functions
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  return format(new Date(dateString), 'MMMM d, yyyy');
}

function formatStatus(status) {
  if (!status) return 'N/A';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

function formatCurrency(value) {
  if (!value && value !== 0) return 'P 0.00';
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'P',
    minimumFractionDigits: 2
  }).format(value || 0);
} 